// src/components/GenerateMobileAppCredentialsModal.js
import React, { useState, useEffect } from "react";
import BackButton from "./BackButton";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../config";
import { FaTimes, FaCheck, FaEdit, FaSpinner } from "react-icons/fa";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useAuth } from "../contexts/AuthContext";
import "./GenerateMobileAppCredentialsModal.css";

import InitialFeedbackModal from "./InitialFeedbackModal"; // Import InitialFeedbackModal
import feedbackService from "../services/feedbackService";

const GenerateMobileAppCredentialsModal = ({ isOpen, onClose }) => {
  const { encryptedData } = useAuth();
  const [memberDetails, setMemberDetails] = useState({
    name: "",
    email: "",
    mobile: "",
    isUpdating: false,
  });
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  const [isInitialFeedbackModalOpen, setIsInitialFeedbackModalOpen] = useState(false); // State for InitialFeedbackModal

  useEffect(() => {
    if (isOpen) fetchMemberDetails();
  }, [isOpen]);

  const fetchMemberDetails = async () => {
    setLoading(true);
    try {
      const payload = {
        data: encryptedData,
      };

      const response = await axios.post(
        `${API_BASE_URL}/x1bf/x1wh/member/members-credentials`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        }
      );

      if (response.data.status === "success") {

        // Check if feedback has not been submitted
        // const feedbackSubmitted = localStorage.getItem('feedbackSubmitted') === 'true';
        // if (!feedbackSubmitted) {
        //   setIsInitialFeedbackModalOpen(true); // Open InitialFeedbackModal
        // }

        setMemberDetails({
          name: response.data.data.name,
          email: response.data.data.email,
          mobile: response.data.data.mobile,
          isUpdating: false,
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: response.data.message,
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#ef4444",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Failed to fetch member details.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateClick = async () => {
    const result = await Swal.fire({
      title: "Confirm Generation",
      text: "Are you sure you want to generate now?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Generate",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#ef4444",
    });

    if (!result.isConfirmed) return;

    // Ask how the user wants to receive their credentials
    const methodResult = await Swal.fire({
      title: "Select Delivery Method",
      text: "How would you like to receive your username and password?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Email",
      cancelButtonText: "SMS",
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#ef4444",
    });

    let deliveryMethod = "email";
    if (!methodResult.isConfirmed) {
      deliveryMethod = "sms";
    }

    // More descriptive variable name
    let deliveryChannel = deliveryMethod === "email" ? "email address" : "mobile number";

    setButtonLoading(true);
    try {
      const payload = {
        data: encryptedData,
        email: memberDetails.email,
        mobile: memberDetails.mobile,
        delivery_method: deliveryMethod,
      };

      const response = await axios.post(
        `${API_BASE_URL}/x1bf/x1wh/member/members-credentials-send`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        }
      );

      if (response.data.status === "success") {
        Swal.fire({
          title: "Success!",
          text: `Your credentials have been securely sent to your ${deliveryChannel}.`,
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#4f46e5",
        }).then(async () => {
          // Check if feedback has not been submitted
          const hasSubmitted = await feedbackService.hasSubmittedFeedback(encryptedData);
          if (!hasSubmitted) {
            setIsInitialFeedbackModalOpen(true); // Open InitialFeedbackModal
          }
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: response.data.message,
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#ef4444",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Failed to generate credentials.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setButtonLoading(false);
    }
  };

  const handleUpdateDetails = async () => {
    const { email, mobile } = memberDetails;

    const result = await Swal.fire({
      title: "Confirm Update",
      html: `<p>Are you sure you want to update your details?</p><p><strong>Email:</strong> ${email}</p><p><strong>Mobile:</strong> ${mobile}</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Update",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#ef4444",
    });

    if (!result.isConfirmed) return;

    setButtonLoading(true);
    try {
      const payload = {
        data: encryptedData,
        email,
        mobile,
      };

      const response = await axios.post(
        `${API_BASE_URL}/x1bf/x1wh/member/members-credentials-update`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        }
      );

      if (response.data.status === "success") {
        Swal.fire({
          title: "Success!",
          text: response.data.message,
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#4f46e5",
        }).then(() => {

          // Check if feedback has not been submitted
          const feedbackSubmitted = localStorage.getItem('feedbackSubmitted') === 'true';
          if (!feedbackSubmitted) {
            setIsInitialFeedbackModalOpen(true); // Open InitialFeedbackModal
          }

          fetchMemberDetails();
          setMemberDetails((prev) => ({ ...prev, isUpdating: false }));
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: response.data.message,
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#ef4444",
        });
      }
    } catch (error) {
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        let errorMessages = "<ul class='text-left text-red-600'>";
        Object.keys(errors).forEach((key) =>
          errors[key].forEach((msg) => (errorMessages += `<li>${msg}</li>`))
        );
        errorMessages += "</ul>";
        Swal.fire({
          title: "Validation Errors",
          html: errorMessages,
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#ef4444",
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: "Failed to update details.",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#ef4444",
        });
      }
    } finally {
      setButtonLoading(false);
    }
  };

  const handleCancelUpdate = () => {
    fetchMemberDetails();
    setMemberDetails((prev) => ({ ...prev, isUpdating: false }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <div className="modal-backrow"><BackButton onClick={memberDetails.isUpdating ? handleCancelUpdate : onClose} /></div>
          <h2 className="modal-title">
            {memberDetails.isUpdating
              ? "Update Information"
              : "Generate Mobile Credentials"}
          </h2>
          <button onClick={onClose} className="close-button">
            <FaTimes />
          </button>
        </div>
        <div className="modal-body">
          {memberDetails.isUpdating ? (
            <div className="form-container">
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  value={memberDetails.email}
                  onChange={(e) =>
                    setMemberDetails({
                      ...memberDetails,
                      email: e.target.value,
                    })
                  }
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Mobile Number</label>
                <input
                  type="text"
                  value={memberDetails.mobile}
                  onChange={(e) =>
                    setMemberDetails({
                      ...memberDetails,
                      mobile: e.target.value,
                    })
                  }
                  className="form-input"
                />
              </div>
              <div className="modal-footer">
                <button onClick={handleCancelUpdate} className="cancel-button">
                  <FaTimes className="button-icon" /> Cancel
                </button>
                <button
                  onClick={handleUpdateDetails}
                  disabled={buttonLoading}
                  className="submit-button"
                >
                  {buttonLoading ? (
                    <FaSpinner className="button-icon animate-spin" />
                  ) : (
                    <FaCheck className="button-icon" />
                  )}
                  Update
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="modal-text">Are these details correct?</p>
              <div className="details-container">
                <p className="detail-item">
                  <strong className="detail-label">Name:</strong>{" "}
                  {loading ? (
                    <Skeleton width={200} height={16} />
                  ) : (
                    memberDetails.name
                  )}
                </p>
                <p className="detail-item">
                  <strong className="detail-label">Email:</strong>{" "}
                  {loading ? (
                    <Skeleton width={250} height={16} />
                  ) : (
                    memberDetails.email
                  )}
                </p>
                <p className="detail-item">
                  <strong className="detail-label">Mobile:</strong>{" "}
                  {loading ? (
                    <Skeleton width={180} height={16} />
                  ) : (
                    memberDetails.mobile
                  )}
                </p>
              </div>
              <div className="modal-footer">
                <button
                  onClick={() =>
                    setMemberDetails({ ...memberDetails, isUpdating: true })
                  }
                  className="edit-button"
                >
                  <FaEdit className="button-icon" /> Edit
                </button>
                <button
                  onClick={handleGenerateClick}
                  disabled={buttonLoading}
                  className="submit-button"
                >
                  {buttonLoading ? (
                    <FaSpinner className="button-icon animate-spin" />
                  ) : (
                    <FaCheck className="button-icon" />
                  )}
                  Generate
                </button>
              </div>
            </>
          )}
        </div>
      </div>



      {/* Render InitialFeedbackModal */}
      <InitialFeedbackModal
        isOpen={isInitialFeedbackModalOpen}
        onClose={() => setIsInitialFeedbackModalOpen(false)}
      />

    </div>
  );
};

export default GenerateMobileAppCredentialsModal;